import moment from 'moment'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {useCountdownTimer} from '../../../hooks/useCountdownTimer'
import {OrderDto} from '../../types'
import {OrderCancellationRequestPayload} from '../orderCancellationRequest/types'

import {
  CancellationStatus,
  CancellationTimer,
  OrderCancellationProcessContext
} from './OrderCancellationContext'

// hardcoded value used already in Hub and OnSite
const CANCELLATION_REASON_NOTES = 'cancellation request'

/**
 * Props for OrderCancellationContextProvider component.
 */
interface OrderCancellationContextProviderProps extends React.PropsWithChildren {
  /** Order containing cancellation information in orderManagement.cancellableUntil */
  order: OrderDto
  /**
   * Timer threshold in ISO 8601 duration format (e.g., '04:00:00' for 4 hours).
   * Timer activates when current time is within this duration before cancellableUntil.
   * If undefined, timer will never activate (only cancellation status will be tracked).
   * If '00:00:00', timer activates immediately when order is cancellable.
   *
   * @example '04:00:00' - Timer shows countdown in last 4 hours before deadline
   * @example undefined - No timer, only isCancellable status
   */
  timerThreshold?: string
}

/**
 * Calculate cancellation status based on deadline
 */
const calculateCancellationStatus = (cancellableUntil: string | undefined): CancellationStatus => {
  if (!cancellableUntil) {
    return {isCancellable: false}
  }

  const until = moment.utc(cancellableUntil)
  const isCancellable = moment.utc().isBefore(until)

  if (!isCancellable) {
    return {isCancellable: false}
  }

  return {
    isCancellable: true,
    cancellableUntil
  }
}

/**
 * Provides order cancellation state to its children via React context.
 *
 * **Auto-refresh behavior:**
 * - Immediately recalculates when new order data arrives (cancellableUntil changes)
 * - Uses smart setTimeout to refresh exactly when state will change (threshold start or deadline)
 * - Activates 1-second interval only when timer is active (for countdown display)
 * - No polling when timer is inactive - maximizes performance
 *
 * **Timer threshold behavior:**
 * - `undefined`: Timer never activates, only isCancellable status tracked
 * - `'00:00:00'`: Timer activates immediately when order is cancellable
 * - Duration (e.g., `'04:00:00'`): Timer activates N time before deadline
 *
 * **State transitions:**
 * 1. Not cancellable → Cancellable (new order data with cancellableUntil)
 * 2. Cancellable (timer inactive) → Cancellable (timer active) - when threshold reached
 * 3. Cancellable (timer active) → Not cancellable - when deadline passed
 *
 * @param props - Provider props
 * @param props.order - Order with orderManagement.cancellableUntil property
 * @param props.timerThreshold - When to activate countdown timer (ISO 8601 duration)
 * @param props.children - Child components to receive context
 *
 * @example
 * // With 4-hour timer threshold
 * <OrderCancellationContextProvider order={order} timerThreshold="04:00:00">
 *   <CancellationButton />
 * </OrderCancellationContextProvider>
 *
 * @example
 * // Without timer (only isCancellable status)
 * <OrderCancellationContextProvider order={order}>
 *   <CancellationButton />
 * </OrderCancellationContextProvider>
 */
export const OrderCancellationContextProvider = ({
  children,
  order,
  timerThreshold
}: OrderCancellationContextProviderProps) => {
  const cancellableUntil = order.orderManagement?.cancellableUntil

  // Calculate cancellation status (is order still cancellable?)
  const [cancellationStatus, setCancellationStatus] = useState<CancellationStatus>(() =>
    calculateCancellationStatus(cancellableUntil)
  )

  // Use countdown timer hook for timer logic
  const timer = useCountdownTimer({
    until: cancellableUntil,
    threshold: timerThreshold,
    onExpire: () => {
      // When timer expires, update cancellation status
      setCancellationStatus({isCancellable: false})
    }
  })

  // Update cancellation status when cancellableUntil changes
  useEffect(() => {
    setCancellationStatus(calculateCancellationStatus(cancellableUntil))
  }, [cancellableUntil])

  /**
   * Submit handler that creates cancellation request and delegates to user-provided handler.
   * Builds payload with hardcoded cancellation reason and checksum, passes order ID to the handler.
   */
  const submit = useCallback(
    <T,>(handleRequest: (orderId: string, request: OrderCancellationRequestPayload) => T): T => {
      const request: OrderCancellationRequestPayload = {
        cancelReasonNotes: CANCELLATION_REASON_NOTES,
        checkSum: order.checkSum
      }

      return handleRequest(order.orderId, request)
    },
    [order.checkSum, order.orderId]
  )

  const value = useMemo(() => {
    const cancellationTimer: CancellationTimer = timer.isActive
      ? {
          isTimerActive: true,
          timer: {
            hours: timer.remaining.hours,
            minutes: timer.remaining.minutes,
            seconds: timer.remaining.seconds
          }
        }
      : {isTimerActive: false}

    return {
      processData: order, // Common field across all order processes
      order, // Deprecated but kept for backward compatibility
      submit,
      ...cancellationStatus,
      ...cancellationTimer
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, submit, cancellationStatus, timer])

  return (
    <OrderCancellationProcessContext.Provider value={value}>
      {children}
    </OrderCancellationProcessContext.Provider>
  )
}
